// «Мышцы тренировки»: кнопка в шапке и окно со схемой — какие группы уже работали (основные оранжевым,
// вспомогательные янтарным), какие ещё впереди (бледные), и список с выполненными подходами.
// Считается на устройстве из упражнений тренировки и справочника — работает и без связи.

import { useMemo, useState } from 'react'

import { useBodySex } from '@/api/settings'
import { MuscleLegend, MuscleMap } from '@/components/muscle-map/MuscleMap'
import type { MuscleTone } from '@/components/muscle-map/muscles'
import { IconButton, Modal } from '@/components/ui'
import { cn } from '@/lib/cn'
import { pluralRu } from '@/lib/format'
import type { ActiveWorkout } from '@/offline/activeSession'
import { useExercises, useMuscleLabels } from '@/pages/workouts/api'

import { workoutMuscles, type WorkoutMuscle } from './logic'

const SETS = ['подход', 'подхода', 'подходов'] as const

function tone(m: WorkoutMuscle): MuscleTone {
  return m.done > 0 ? m.role : 'planned'
}

export function MusclesButton({ workout, className }: { workout: ActiveWorkout; className?: string }) {
  const [open, setOpen] = useState(false)
  return (
    <>
      <IconButton
        icon="character"
        label="Мышцы тренировки"
        variant="secondary"
        className={cn('-my-1.5 shrink-0', className)}
        onClick={() => setOpen(true)}
      />
      {open ? <MusclesSheet workout={workout} onClose={() => setOpen(false)} /> : null}
    </>
  )
}

function MusclesSheet({ workout, onClose }: { workout: ActiveWorkout; onClose: () => void }) {
  const sex = useBodySex()
  const label = useMuscleLabels()
  const catalog = useExercises().data
  const [selected, setSelected] = useState<string | null>(null)

  const muscles = useMemo(() => {
    const byId = new Map((Array.isArray(catalog) ? catalog : []).map((e) => [e.id, e]))
    return workoutMuscles(workout, byId)
  }, [workout, catalog])
  const tones = useMemo(
    () => Object.fromEntries(Object.entries(muscles).map(([k, m]) => [k, tone(m)])),
    [muscles],
  )
  const list = Object.entries(muscles).sort(
    ([, a], [, b]) =>
      Number(b.role === 'primary') - Number(a.role === 'primary') || b.done - a.done || b.total - a.total,
  )
  const picked = selected ? muscles[selected] : undefined

  return (
    <Modal open onClose={onClose} title="Мышцы тренировки" className="sm:max-w-lg" focusPanel>
      {list.length === 0 ? (
        <p className="text-sm text-muted">Добавь упражнения — здесь появятся мышцы, которые они качают.</p>
      ) : (
        <div className="flex flex-col gap-3">
          <MuscleMap
            sex={sex}
            tones={tones}
            selected={selected}
            onSelect={setSelected}
            label={`Работают: ${list.map(([k]) => label(k)).join(', ')}`}
            className="mx-auto w-full max-w-sm"
          />
          <MuscleLegend tones={['primary', 'secondary', 'planned']} className="justify-center" />
          <p className="min-h-10 rounded-lg bg-surface-2 px-3 py-2 text-sm" aria-live="polite">
            {selected ? (
              <>
                <span className="font-medium">{label(selected)}</span> ·{' '}
                {picked
                  ? `${picked.role === 'primary' ? 'основная' : 'вспомогательная'}, ${picked.done} из ${picked.total} ${pluralRu(picked.total, SETS)}`
                  : 'в этой тренировке не работает'}
              </>
            ) : (
              <span className="text-muted">Нажми на мышцу, чтобы увидеть подробности</span>
            )}
          </p>
          <ul aria-label="Группы мышц тренировки" className="flex flex-col">
            {list.map(([key, m]) => (
              <li key={key} className="flex items-center gap-2 border-b border-border py-1.5 text-sm last:border-0">
                <span
                  aria-hidden
                  className="size-2.5 shrink-0 rounded-full"
                  style={{ background: `var(--muscle-${m.role})`, opacity: m.done > 0 ? 1 : 0.35 }}
                />
                <span className={cn('flex-1', m.role === 'primary' ? 'font-medium' : 'text-muted')}>{label(key)}</span>
                <span className={cn('tabular-nums', m.total > 0 && m.done === m.total ? 'text-accent' : 'text-muted')}>
                  {m.done}/{m.total}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </Modal>
  )
}
